import { StorageLocationDto } from '@nest/domain-entities-metadata-generated';
import { usePrintActionHandler } from '@nest/internal-frontend';
import { Button, Label, Tabs, TabsContent, TabsList, TabsTrigger, useScanner, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileTextIcon, HomeIcon, PackageIcon, PrinterIcon } from 'lucide-react';
import { useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useTerminalClient } from '../client-rpc-router';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function OutboundDeliveryOrderProcessPage() {
	const [documentTypeId] = useQueryState('documentTypeId');
	const [queueId] = useQueryState('queueId');
	const outboundDeliveryOrderId = useNumberParam('id');
	const client = useTerminalClient();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [activeTab, setActiveTab] = useState<'remainingItems' | 'loadedItems'>('remainingItems');
	const [destinationLocation, setDestinationLocation] = useState<StorageLocationDto | null>(null);
	const scanner = useScanner();
	const [bottomElementRef, setBottomElementRef] = useState<HTMLDivElement | null>(null);

	const printActionHandler = usePrintActionHandler(
		'outboundDocumentPrintInvoicesByShipmentRoute',
		(numberOfCopies, printerIds) => {
			return client.outboundDeliveryOrders.printOutboundDocumentByOutboundDeliveryOrderIdQuery.execute({
				outboundDeliveryOrderId: outboundDeliveryOrderId!,
				numberOfCopies,
				printerIds
			});
		},
		{
			allowedActions: ['print']
		}
	);

	const startLineProcessMutation = useMutation({
		mutationFn: (lineId: number) => client.outboundDeliveryOrders.startOutboundDeliveryOrderLineProcessMutation.execute({ id: lineId }),
		onSuccess: (_data, lineId) => {
			navigate(`/outbound/delivery/orders/${outboundDeliveryOrderId}/process/line/${lineId}`);
		}
	});

	const { data: order, error } = useQuery({
		queryKey: ['outbound-order', outboundDeliveryOrderId],
		queryFn: () =>
			client.outboundDeliveryOrders.getById.execute({
				id: outboundDeliveryOrderId!,
				populate: ['lines', 'lines.stockItem', 'lines.stockItem.item', 'documentType']
			}),
		enabled: !!outboundDeliveryOrderId
	});

	const remainingLines = useMemo(() => (order?.lines ?? []).filter(l => (l.quantityPickRemaining ?? 0) > 0), [order?.lines]);
	const loadedLines = useMemo(() => (order?.lines ?? []).filter(l => (l.quantityNowLoaded ?? 0) > 0), [order?.lines]);
	const allItemsCompleted = useMemo(() => {
		if (!order?.lines) {
			return false;
		}

		return order.lines.every(l => (l.quantityPickRemaining ?? 0) === 0 && (l.quantityNowLoaded ?? 0) === 0);
	}, [order?.lines]);

	const openLine = (lineId: number) => {
		startLineProcessMutation.mutate(lineId);
	};

	const unloadAllMutation = useMutation({
		mutationFn: () =>
			client.outboundDeliveryOrders.outboundDeliveryOrderProcessUnloadAllMutation.execute({ id: outboundDeliveryOrderId!, destinationLocationId: destinationLocation!.id }),
		onSuccess: () => {
			setActiveTab('loadedItems');
			queryClient.invalidateQueries({ queryKey: ['outbound-order', outboundDeliveryOrderId] });
			toast.success('Položky byly vyloženy na cílovou lokaci');
		}
	});

	useValidationResultDialog([error, unloadAllMutation.error, startLineProcessMutation.error]);

	useEffect(() => {
		if (activeTab !== 'remainingItems') {
			return;
		}

		const scannedCode = scanner.code;
		if (!scannedCode) {
			return;
		}

		const foundLine = (order?.lines ?? []).find(l => l.stockItem?.item?.code === scannedCode);
		if (foundLine) {
			startLineProcessMutation.mutate(foundLine.id);
		}
	}, [scanner.code, activeTab, order?.lines, startLineProcessMutation, navigate, outboundDeliveryOrderId]);

	let content: React.ReactNode;

	if (allItemsCompleted) {
		content = (
			<>
				<div className="bg-yellow-100 text-slate-900 p-4 m-2">Všechny položky byly naloženy a vyloženy na cílové lokaci</div>
				<div className="h-full p-2 flex flex-col gap-2">
					<Button variant="outline" size="xl" onClick={() => navigate(`/`)}>
						<HomeIcon className="w-4 h-4" />
						Přejít na úvod
					</Button>
					<Button variant="outline" size="xl" onClick={() => printActionHandler.run()}>
						<PrinterIcon className="w-4 h-4" />
						Tisk dokladu
					</Button>
					<Button variant="outline" size="xl" onClick={() => navigate(`/outbound/delivery/orders/${outboundDeliveryOrderId}`)}>
						<FileTextIcon className="w-4 h-4" />
						Detail objednávky
					</Button>
					{order?.documentType.packingType && (
						<Button variant="outline" size="xl" onClick={() => navigate(`/outbound/delivery/orders/${outboundDeliveryOrderId}/process/pack`)}>
							<PackageIcon className="w-4 h-4" />
							Ručně zabalit
						</Button>
					)}
				</div>
			</>
		);
	} else {
		content = (
			<div className="h-full p-2 flex flex-col">
				<Tabs value={activeTab} onValueChange={v => setActiveTab(v === 'loadedItems' ? 'loadedItems' : 'remainingItems')} className="flex-1">
					<TabsContent value="remainingItems" className="p-2 space-y-2">
						{(remainingLines ?? []).map(line => (
							<button key={line.id} className="w-full text-left border rounded p-2 hover:bg-muted/50" onClick={() => openLine(line.id)}>
								<div className="font-semibold">{line.stockItem?.item?.name}</div>
								<div className="text-sm text-muted-foreground">Vyskladnit: {line.quantityPickRemaining ?? 0} ks</div>
							</button>
						))}
					</TabsContent>

					<TabsContent value="loadedItems" className="space-y-5">
						<Label className="block">Cílová lokace</Label>
						<StorageLocationScanInput placeholder="Naskenujte cílovou lokaci..." autoFocus value={destinationLocation} onChange={setDestinationLocation} />

						<Label className="mb-1 block">Naložené položky</Label>
						<div className="mt-2 space-y-2 divide-y gap-2 p-2 rounded-md border border-input-border">
							{(loadedLines ?? []).map(line => (
								<div key={line.id} className="w-full flex items-center gap-3">
									<div className="rounded-md bg-slate-100 px-2 py-1 text-sm font-semibold">{line.quantityNowLoaded ?? 0}ks</div>
									<div className="font-semibold">{line.stockItem?.item?.name}</div>
								</div>
							))}
						</div>

						<div className="mt-2 sticky bottom-0 bg-background">
							<Button className="w-full" onClick={() => unloadAllMutation.mutate()} disabled={unloadAllMutation.isPending || !destinationLocation}>
								Vyložit vše
							</Button>
						</div>
					</TabsContent>

					{bottomElementRef &&
						createPortal(
							<TabsList className="grid grid-cols-2 w-full">
								<TabsTrigger value="remainingItems" className="w-full">
									Nezpracované ({remainingLines.length})
								</TabsTrigger>
								<TabsTrigger value="loadedItems" className="w-full">
									Naložené ({loadedLines.length})
								</TabsTrigger>
							</TabsList>,
							bottomElementRef
						)}
				</Tabs>
			</div>
		);
	}

	return (
		<TerminalScreen
			showBackButton
			backButtonTo={`/outbound/delivery/orders/${outboundDeliveryOrderId}?documentTypeId=${documentTypeId}&queueId=${queueId}`}
			topBar={<div>{order?.documentNumber ?? ''}</div>}
			bottomContent={<div ref={setBottomElementRef} />}
		>
			{content}
		</TerminalScreen>
	);
}
